import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filterNil } from '@ngneat/elf';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { Mutex } from 'async-mutex';
import { fabric } from 'fabric';
import { IEvent } from 'fabric/fabric-impl';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  combineLatest,
  filter,
  first,
  firstValueFrom,
  fromEvent,
  of,
  startWith,
} from 'rxjs';
import { auditTime, distinctUntilChanged, map, pairwise, retry, take, tap } from 'rxjs/operators';
import { ManagerOptions, SocketOptions } from 'socket.io-client';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';
import {
  MouseFrameEventType,
  MovedBoardsPlacement,
  SyncService,
  WebSocketEventType,
  WhiteboardServerErrors,
} from '../common/interfaces/sync-service-interface';
import { notEmpty } from '../common/utils/common-util';
import { ERRORS, SUCCESSES } from '../common/utils/notification-constants';
import { AiContentProcessor } from '../genai/AiContentProcessor';
import { GestureName } from '../hand-gestures/models/gesture';
import { AnalyticsEventType } from '../models/analytics';
import { ConnectionStatus } from '../models/network';
import {
  BoardFolder,
  Frame,
  FrameItem,
  ISession,
  Room,
  Session,
  permissionsSchema,
} from '../models/session';
import {
  FrameEvent,
  FrameEventType,
  FrameObservableData,
  GcsSpaceDownload,
  Mouse,
  Point,
  ServerStateUpdate,
  SessionAuth,
  SessionUpdate,
  SessionsUpdate,
  SpaceChunk,
  SpaceChunkInfo,
  SpaceDownloadEnd,
  SpaceDownloadStart,
  UserEvent,
} from '../models/session-sync';
import { User } from '../models/user';
import { Manager } from '../sessions/common/session-data-structures';
import { convertFrameItemToYCanvasItem } from '../sessions/common/sync.util';
import {
  SessionMetadata,
  UndoEnum,
  YCanvasItemObject,
  YSession,
} from '../sessions/common/y-session';
import {
  EventCategory,
  SessionEvent,
  SessionEventController,
  SessionEventSubscriber,
} from '../sessions/session/SessionEvent';
import {
  BrowserLifecycleEvent,
  PencilSocket,
  SocketConnectionEvent,
  SocketManagerEvent,
} from '../socket-utilities/pencil-socket';
import { SocketIOWorkerWrapper, SocketIOWrapper } from '../socket-utilities/socket-io-wrapper';
import { LeaderModeRepositoryService } from '../state/leader-mode-repository.service';
import { SpaceRepository, filterNotSynced } from '../state/space.repository';
import {
  TemporaryUserMetadataEntry,
  TemporaryUserMetadataEntryBase,
  TemporaryUserMetadataEntryRedis,
  TemporaryUserMetadataRepositoryService,
} from '../state/temporary-user-metadata-repository.service';
import { ObservableUtils } from '../utilities/ObservableUtils';
import {
  enterZone,
  modifiedSetInterval,
  modifiedSetTimeout,
  modifiedTimer,
} from '../utilities/ZoneUtils';
import { ExpiringSet } from '../utilities/expiring-set';
import { SpaceUtils } from '../utilities/space.utils';
import { MainToolbarService } from '../sessions/session/wb-main-toolbar-v2/services/main-toolbar.service';
import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { ConstantMemoryApiSpaceSyncSettingsService } from '../constant-memory-api-space-sync-settings.service';
import { Location as UserLocation } from '../state/temporary-user-metadata-repository.service';
import { isScreenShareAnnotationsSpaceID } from '../sessions/common/session-call-user/screen-share-annotations/screen-share-annotations.utils';
import { ConfirmationModalComponent } from '../standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { AnalyticsService } from './analytics.service';
import { FLAGS, FlagsService } from './flags.service';
import { ClientNetworkState, NetworkService } from './network.service';
import { NotificationToasterService } from './notification-toaster.service';
import { BoardItemsEncoderDecoder } from './board-items-encoder-decoder.service';
import { CountedMetric, ExternalMetric } from './performance.logger.service';
import { PresenceData, PresenceRoom } from './presence_v2.service';
import { RealtimeService } from './realtime.service';
import { ScribbleType, SessionSharedDataService } from './session-shared-data.service';
import { SpacesService } from './spaces.service';
import { emojiLibraryIndex } from './ui.service';
import { UploadFileService } from './upload-file.service';
import { UserIdleService } from './user-idle.service';
import { UserService } from './user.service';
import { VideoAIService } from './video-ai.service';
import { WhiteboardUrlService } from './whiteboard-url.service';
import { ActiveCanvasStateService, ActiveCanvasType } from './active-canvas-state.service';
import { ObserverModeService } from './observer-mode.service';

export enum NetworkStatus {
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
}

// Socket connection event types for immediate notification
export enum SocketEventType {
  CONNECT = 'connect',
  RECONNECT = 'reconnect',
  DISCONNECT = 'disconnect',
}

const SERVER_ORIGIN = 'SERVER_ORIGIN';

class ChunkedSpace {
  static readonly CHUNK_SIZE = 1024 * 1024;
  private _spaceBuffer!: ArrayBufferLike;
  private _spaceContext!: ISession;
  private totalSize = 0;
  private readonly spaceDownloadProgress$: Subject<number> = new Subject<number>();
  private spaceUtils = new SpaceUtils();

  constructor(
    private socket: PencilSocket<WebSocketEventType>,
    spaceId: string,
    spaceSize: number,
    spaceContext: ISession,
  ) {
    this.socket = socket;
    this._spaceContext = spaceContext;
    this.totalSize = spaceSize;
    this._spaceBuffer = new Uint8Array();
    const spaceChunkInfo: SpaceChunkInfo = {
      spaceId,
      receivedBytes: 0,
      chunkSize: ChunkedSpace.CHUNK_SIZE,
    };
    this.socket.emit(WebSocketEventType.GET_SPACE_CHUNK, spaceChunkInfo);
    this.spaceDownloadProgress$.next(0);
  }

  appendChunk(spaceId: string, chunk: ArrayBuffer) {
    const currentChunk = new Uint8Array(this.spaceBuffer);
    const newChunk = new Uint8Array(chunk);
    this._spaceBuffer = this.spaceUtils.concatChunks([currentChunk, newChunk]);
    this.spaceDownloadProgress$.next(
      Math.ceil((this.spaceBuffer.byteLength / this.totalSize) * 100),
    );
    const spaceChunkInfo: SpaceChunkInfo = {
      spaceId,
      receivedBytes: this.spaceBuffer.byteLength,
      chunkSize: ChunkedSpace.CHUNK_SIZE,
    };
    this.socket.emit(WebSocketEventType.GET_SPACE_CHUNK, spaceChunkInfo);
  }

  resetDownloadProgress() {
    this.spaceDownloadProgress$.next(0);
  }

  public get spaceBuffer(): ArrayBufferLike {
    return this._spaceBuffer;
  }

  public get spaceContext() {
    return this._spaceContext;
  }

  public getSpaceDownloadProgressObservable(): Observable<number> {
    return this.spaceDownloadProgress$.asObservable();
  }
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SessionsSyncService extends SyncService implements SessionEventController, OnDestroy {
  /** Public members */

  /**
   * @deprecated backward compatibility
   */
  public get userJoinedEvent$(): Observable<Array<PresenceData>> {
    return this.onUserJoinPresenceEvent.pipe(enterZone(this.zone));
  }

  /**
   * @deprecated backward compatibility
   */
  public get userLeftEvent$(): Observable<Array<PresenceData>> {
    return this.onUserLeavePresenceEvent.pipe(enterZone(this.zone));
  }

  /**
   * @deprecated backward compatibility
   */
  public get userDisconnectedEvent$(): Observable<Array<PresenceData>> {
    return this.onUserDisconnectedPresenceEvent.pipe(enterZone(this.zone));
  }

  public get presenceUpdate$(): Observable<Array<PresenceRoom>> {
    return this.onPresenceUpdate.pipe(enterZone(this.zone));
  }

  public get userTemporaryMetadataUpdate$(): Observable<Array<TemporaryUserMetadataEntryRedis>> {
    return this.onUserTemporaryMetadataUpdate.pipe(enterZone(this.zone));
  }

  public get reconnectAttempt(): number {
    return this._reconnectAttempt;
  }

  public get socketIdHistory(): Set<string> {
    return this._socketIdHistory;
  }

  public get isOffline(): boolean {
    return this._isOffline;
  }

  // Public observables

  public get realTimeEvent$(): Observable<{ mouse: Mouse; user: string }[]> {
    return this.realTimeEventSubject;
  }

  public get undoStack$(): Observable<number> {
    return this.undoStackSubject.pipe(enterZone(this.zone));
  }

  public get redoStack$(): Observable<number> {
    return this.redoStackSubject.pipe(enterZone(this.zone));
  }

  public get usersLeftFrame$(): Observable<string[]> {
    return this.usersLeftFrame.pipe(enterZone(this.zone));
  }

  public get usersJoinedFrame$(): Observable<string[]> {
    return this.usersJoinedFrame.pipe(enterZone(this.zone));
  }

  public get userEvents$(): Observable<UserEvent | undefined> {
    return this.userEvents.pipe(startWith(undefined));
  }

  public get viewportEvent$(): Observable<{
    user: string;
    location: UserLocation;
    userName?: string;
  }> {
    return this.viewportEventSubject.asObservable();
  }

  public get updateUserPresence$(): Observable<'reconnected' | 'disconnected'> {
    return this.updateUserPresence.pipe(enterZone(this.zone));
  }

  public get networkStatusObservable$(): Observable<NetworkStatus> {
    return this._networkStatusObservable;
  }

  // Provides immediate socket connection events (connect, reconnect, disconnect)
  // Unlike reconnected$ which fires after a 1-second delay (due to updateUserPresence)
  public get wbSocketEvents$(): Observable<SocketEventType> {
    return this.wbSocketEventSubject.asObservable();
  }

  /** Private members */

  // Signals when a user performs mouse activity on the canvas, or changes frames
  private readonly realTimeEventSubject: Subject<{ mouse: Mouse; user: string }[]> = new Subject();

  // Signals the number of element in the undo/redo stack on change.
  private readonly undoStackSubject = new BehaviorSubject<number>(0);
  private readonly redoStackSubject = new BehaviorSubject<number>(0);

  // Signals when a user joins or leaves a frame
  private readonly usersLeftFrame: Subject<string[]> = new Subject<string[]>();
  private readonly usersJoinedFrame: Subject<string[]> = new Subject<string[]>();

  // private readonly inZoneScheduler = enterZoneScheduler(this.zone);

  // Subject that produces all userEvents coming from remote clients
  private readonly userEvents: Subject<UserEvent> = new Subject<UserEvent>();
  // Emits viewport updates from realtime frame events
  private readonly viewportEventSubject: Subject<{
    user: string;
    location: UserLocation;
    userName?: string;
  }> = new Subject();

  private readonly ySessions: Manager<YSession>;

  public socket?: PencilSocket<WebSocketEventType>;

  private user?: User;
  private disconnected = true;
  private _reconnectAttempt = 0; // Count reconnect attemps
  private _socketIdHistory: Set<string> = new Set(); // Store socketID for every succussful connection attemp
  private _isOffline = false;

  private heartbeatInterval: NodeJS.Timer;

  // backward compatibility
  private onUserJoinPresenceEvent = new ReplaySubject<Array<PresenceData>>();
  // backward compatibility
  private onUserLeavePresenceEvent = new ReplaySubject<Array<PresenceData>>();
  // backward compatibility
  private onUserDisconnectedPresenceEvent = new ReplaySubject<Array<PresenceData>>();

  private onPresenceUpdate = new ReplaySubject<Array<PresenceRoom>>();

  private onUserTemporaryMetadataUpdate = new Subject<Array<TemporaryUserMetadataEntryRedis>>();

  // The current active session
  private activeSessionId?: string;

  // The rooms that have been joined by the socket server
  private joinedRooms: string[] = [];

  private rtFrameSubscription?: Subscription;
  private roomsSubscription?: Subscription;

  private sessionSubscriptions?: Subscription;

  private eventRegistry = {};

  // Needed to reconnect when we disconnect from the socket
  private userRoom?: string;
  private changeOfflineStateMutex = new Mutex();

  private sendUserMetadataUpdateMutex = new Mutex();
  private numberOfUserMetadataUpdatesQueue = 0;

  private userFaceDetected = true;
  private timerArray: Array<any> = [];
  private prevEmojiId = -1;
  private animationStartTime = 0;

  private readonly YDOC_PENDING_CHANGES_DELAY = 3000;
  private readonly WB_SERVER_ACK_TIMEOUT = 5000;
  private readonly WB_SERVER_ACK_TIMEOUT_THROTTLE = 6000;
  private readonly CURRENT_SESSION_HEARTBEAT_THROTTLE = 60000; // 1 minute
  private readonly CONSISTENCY_THRESHOLD = 3;
  private readonly DEFAULT_SOCKET_EVENT_PROCESS_PERIOD = 1000;

  private readonly wbServerAckTimeoutSubject: Subject<boolean> = new Subject<boolean>();
  private chunkedSpace!: ChunkedSpace;
  private _spaceDownloadProgress$: Subject<number> = new Subject<number>();

  private webSocketWorkerEnabled = false;
  private socketId!: string;

  private updateUserPresence: ReplaySubject<'disconnected' | 'reconnected'> = new ReplaySubject();
  private _syncSessionDataSubject = new Subject<void>();
  private readonly SYNC_SESSION_DATA_THROTTLE = 2000;
  private _syncSessionDataMutex = new Mutex();

  private networkStatus = new Subject<NetworkStatus>();
  private _networkStatusObservable = this.networkStatus.asObservable();

  private syncsInProgress = new ExpiringSet<string>();
  private readonly SYNC_TIMEOUT = 2 * 60 * 1_000; // 2 minutes
  private readonly GCS_DOWNLOAD_MAX_RETRIES = 5;
  private readonly GCS_DOWNLOAD_BACKOFF_BASE_MS = 500;
  private readonly GCS_DOWNLOAD_BACKOFF_MAX_MS = 5_000;

  // Timers to re-trigger sync if it didn't complete within SYNC_TIMEOUT
  private syncTimeoutTimers = new Map<string, any>();

  // Used to emit whiteboard socket connection events immediately when they occur
  // Unlike updateUserPresence which emits after a 1-second delay
  // TODO: Eventually replace sharedDataService.socketReconnected with this more
  // comprehensive event system to maintain a single source of truth for socket events
  private wbSocketEventSubject = new Subject<SocketEventType>();

  private pendingYDocScribbleUids: string[] = [];

  private gcsFailureModalRef?: MatDialogRef<ConfirmationModalComponent>;

  private readonly debouncedSendYUpdateHandlers = new Map();

  /** Public functions */

  constructor(
    private sharedDataService: SessionSharedDataService,
    private flagsService: FlagsService,
    private userService: UserService,
    private zone: NgZone,
    private whiteboardUrlService: WhiteboardUrlService,
    private networkService: NetworkService,
    private uploadService: UploadFileService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private videoAIService: VideoAIService,
    private observerUtils: ObservableUtils,
    private spaceRepo: SpaceRepository,
    private leaderModeRepositoryService: LeaderModeRepositoryService,
    private temporaryUserMetadataRepository: TemporaryUserMetadataRepositoryService,
    private analyticsService: AnalyticsService,
    private spacesService: SpacesService,
    private userIdleService: UserIdleService,
    private telemetry: TelemetryService,
    private realtimeService: RealtimeService,
    private http: HttpClient,
    private constantMemoryApiSpaceSyncSettingsService: ConstantMemoryApiSpaceSyncSettingsService,
    private mainToolbarService: MainToolbarService,
    private boardItemsEncoderDecoder: BoardItemsEncoderDecoder,
    private activeCanvasStateService: ActiveCanvasStateService,
    private observerModeService: ObserverModeService,
    private matDialog: MatDialog,
  ) {
    super();

    this.flagsService.featureFlagsChanged.pipe(first()).subscribe(() => {
      // Update the rate at which socket events are processed
      this.socketEventProcessRate =
        (this.flagsService.featureFlagsVariables.event_buffering
          .receive_data_buffer_ms as number) ?? this.DEFAULT_SOCKET_EVENT_PROCESS_PERIOD; // default to 1000ms
      this.realtimeEventProcessRate =
        (this.flagsService.featureFlagsVariables.event_buffering
          .receive_realtime_buffer_ms as number) ?? 30; // default to 30ms
    });

    this.ySessions = new Manager<YSession>((sessionId) => {
      const flagsConfigs = {
        useFabricJson: this.flagsService.isFlagEnabled(FLAGS.ENABLE_YJS_FABRIC_JSON),
        breakoutRoomsEnabled: this.flagsService.isFlagEnabled(FLAGS.BREAKOUT_ROOMS),
        pendingStructsSyncThreshold: this.flagsService.featureFlagsVariables
          .limit_missing_update_sync?.pending_structs_sync_threshold as number | undefined,
        canvasItemsKeysDeletion: this.flagsService?.isFlagEnabled(FLAGS.CANVAS_ITEMS_KEYS_DELETION),
        enableYSessionDiagnosticLogging: this.flagsService?.isFlagEnabled(
          FLAGS.ENABLE_YSESSION_DIAGNOSTIC_LOGGING,
        ),
      };
      const ySession = new YSession(flagsConfigs, this.telemetry, this.boardItemsEncoderDecoder);
      ySession.updateObservable$.subscribe((e) => {
        //  Because array duplication of frames in yjs is quite common,
        // every time there is an update we attempt to fix the duplicated frames
        const didFrameDuplicationOccur = ySession.deduplicateYFrames(e.frames);

        if (didFrameDuplicationOccur) {
          this.calcDiffAndSendUpdate(sessionId);
        }

        if (didFrameDuplicationOccur) {
          this.telemetry.event('Frame duplication occurred, its been fixed');
        }

        this.spaceRepo.updateSpaceYjs(sessionId, e);
      });

      if (this.constantMemoryApiSpaceSyncSettingsService.shouldSendUpdatesBasedOnDocChanges) {
        this.setupYDocUpdateListener(ySession, sessionId);
      }
      return ySession;
    });
    this.sharedDataService.sessionEventController = this;

    this.userService.user.subscribe((data) => {
      if (data && data?.user) {
        this.user = data.user;
      }
    });

    this.heartbeatInterval = modifiedSetInterval(() => {
      if (this.activeSessionId && this.activeSessionId !== '') {
        this.sendHeartbeat();
      }
    }, 300000);
    this.handleConnectionStatus();

    this.videoAIService.faceObservable().subscribe((faceDetected) => {
      if (faceDetected !== this.userFaceDetected) {
        const faceEvent = new SessionEvent(
          EventCategory.Users,
          'users:face-detected',
          faceDetected,
        );
        this.sendEvent(this, faceEvent);
      }
      this.userFaceDetected = faceDetected;
    });

    this.videoAIService
      .emoticonObservable()
      .pipe(distinctUntilChanged(), auditTime(1000))
      .subscribe((gesture: GestureName) => {
        this.analyticsService.addToAnalyticsEventsBatch(AnalyticsEventType.REACTIONS);
        this.sendEmote(gesture);
      });

    this.analyticsService.getCurrentSessionId$.subscribe(() => this.getCurrentAnalyticsSession());

    this.realtimeService.realtimeDisconnected$
      .pipe(untilDestroyed(this), distinctUntilChanged(), pairwise())
      .subscribe(async ([previousrealtimeDisconnected, currentrealtimeDisconnected]) => {
        if (currentrealtimeDisconnected) {
          this.telemetry.event('realtimeDisconnected');
        }
        // Sync session data in case realtime update is missed
        if (
          this.spaceRepo.activeSpaceId &&
          previousrealtimeDisconnected === true &&
          currentrealtimeDisconnected === false
        ) {
          this._syncSessionDataSubject.next();
        }
      });

    this.observerUtils
      .throttledObservableOf(this._syncSessionDataSubject, this.SYNC_SESSION_DATA_THROTTLE)
      .pipe(untilDestroyed(this))
      .subscribe(async () => {
        await this._syncSessionDataMutex.runExclusive(async () => {
          if (this.spaceRepo.activeSpaceId) {
            await this.syncSessionData(this.spaceRepo.activeSpaceId);
          }
        });
      });
  }

  ngOnDestroy(): void {
    clearInterval(this.heartbeatInterval);
  }

  private clearResyncTimeout(sessionId: string): void {
    const existingTimer = this.syncTimeoutTimers.get(sessionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.syncTimeoutTimers.delete(sessionId);
    }
  }

  private clearSyncState(sessionId: string): void {
    this.syncsInProgress.remove(sessionId);
    this.clearResyncTimeout(sessionId);
  }

  private scheduleResyncOnTimeout(sessionId: string): void {
    if (!this.flagsService.isFlagEnabled(FLAGS.RESYNC_ON_TIMEOUT)) {
      return;
    }
    this.clearResyncTimeout(sessionId);
    const timerId = modifiedSetTimeout(
      async () => {
        if (await this.socket?.connected()) {
          this.telemetry.event('Re-sync triggered after sync timeout', { sessionId });
          this.syncsInProgress.remove(sessionId);
          this.syncTimeoutTimers.delete(sessionId);
          this.sendSyncWithServer(sessionId, true);
        }
      },
      this.SYNC_TIMEOUT,
      true,
    );
    this.syncTimeoutTimers.set(sessionId, timerId);
  }

  private async _setupSocket(
    url: string,
    opts: Partial<ManagerOptions & SocketOptions>,
  ): Promise<void> {
    const socketWrapper = new SocketIOWrapper(url, opts);
    this.socket = socketWrapper;
  }

  private async _setupWebWorker(
    url: string,
    opts: Partial<ManagerOptions & SocketOptions>,
  ): Promise<void> {
    const socketWrapper: SocketIOWorkerWrapper<WebSocketEventType> = new SocketIOWorkerWrapper(
      url,
      opts,
    );
    this.socket = socketWrapper;

    // setup socket wrapper
    await socketWrapper.setup();

    addEventListener(BrowserLifecycleEvent.ONLINE, async () => {
      const connected = await socketWrapper.connected();
      if (connected) {
        this.sendSyncWithServer();
      }
    });
  }

  // Connect once, then keep the socket for the whole app session. Checks the instance (not
  // connected()) so a transient drop the socket is already auto-reconnecting from isn't torn
  // down and re-created via forceNew.
  public ensureConnected(): void {
    if (this.socket) {
      return;
    }
    this.setupSocket();
  }

  public async setupSocket(): Promise<void> {
    const socketUrl = this.whiteboardUrlService.getWhiteboardUrl();
    const socketPath = this.whiteboardUrlService.getWhiteboardPath();
    const transports = this.flagsService.isFlagEnabled(FLAGS.WB_NO_LONGPOLLING)
      ? ['websocket']
      : ['websocket', 'polling'];
    const dataBufferDuration: number =
      (this.flagsService.featureFlagsVariables.event_buffering.receive_data_buffer_ms as number) ??
      1000; // default to 1000ms
    const realtimeBufferDuration: number =
      (this.flagsService.featureFlagsVariables.event_buffering
        .receive_realtime_buffer_ms as number) ?? 30; // default to 30ms
    this.webSocketWorkerEnabled = this.isWsWorkerEnabled();

    // Run socket subscription outside angular to prevent buffering/signaling from triggering unncessary
    // CD cycles. This make it the responsibility of code in the socket callbacks to notify any public
    // observables "inside zone" so downstream observers can still update their state as part of a CD cycle.
    this.zone.runOutsideAngular(async () => {
      if (this.webSocketWorkerEnabled) {
        await this._setupWebWorker(socketUrl, {
          transports,
          forceNew: true,
          path: socketPath,
        });
      } else {
        await this._setupSocket(socketUrl, {
          transports,
          closeOnBeforeunload: false,
          forceNew: true,
          path: socketPath,
        });
      }

      if (!this.socket) {
        return;
      }

      // Send session heartbeat every 5 minutes to keep current session active
      this.observerUtils
        .throttledObservableOf(
          this.userIdleService.onHeartbeat$,
          this.CURRENT_SESSION_HEARTBEAT_THROTTLE,
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          if (!this.activeSessionId) {
            return;
          }
          this.socket?.emit(WebSocketEventType.CURRENT_SESSION_HEARTBEAT, this.activeSessionId);
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.USER_JOINED),
          realtimeBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          if (data) {
            this.onUserJoinPresenceEvent.next(data);
          }
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.USER_LEFT),
          realtimeBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          if (data) {
            this.onUserLeavePresenceEvent.next(data);
          }
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.USER_DISCONNECTED),
          realtimeBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          if (data) {
            this.onUserDisconnectedPresenceEvent.next(data);
          }
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.PRESENCE_UPDATE),
          realtimeBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          if (data) {
            this.onPresenceUpdate.next(data);
          }
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.USER_TEMPORARY_METADATA_UPDATE),
          realtimeBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          if (data) {
            this.onUserTemporaryMetadataUpdate.next(data);
          }
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.BROADCAST),
          dataBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((updates) => {
          this.applyBulkUpdates(updates as SessionsUpdate[]);
        });

      this.observerUtils
        .bufferedObservableOf(fromEvent(this.socket, WebSocketEventType.UPDATE), dataBufferDuration)
        .pipe(untilDestroyed(this))
        .subscribe((updates) => {
          this.applyBulkUpdates(updates as SessionsUpdate[]);
        });

      // serverStateObservable should be deprecated after using update ack to send serverStateVector
      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.SERVER_STATE_VECTOR),
          dataBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((updates) => {
          this.updateBulkServerStates(updates as ServerStateUpdate[]);
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.FRAME_EVENT),
          realtimeBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((events) => {
          this.handleBulkFrameEvents(events as FrameEvent[]);
        });

      this.observerUtils
        .bufferedObservableOf(
          fromEvent(this.socket, WebSocketEventType.USER_EVENT),
          dataBufferDuration,
        )
        .pipe(untilDestroyed(this))
        .subscribe((events) => {
          for (const event of events) {
            this.userEvents.next(event as UserEvent);
          }
        });

      // throttling to not send many sync requests within short period
      this.observerUtils
        .throttledObservableOf<boolean>(
          // interested only in timeout events
          this.wbServerAckTimeoutSubject.pipe(filter((value) => !!value)),
          this.WB_SERVER_ACK_TIMEOUT_THROTTLE,
        )
        .pipe(untilDestroyed(this))
        .subscribe({
          next: async (next) => {
            if (next && (await this.socket?.connected())) {
              this.sendSyncWithServer();
            }
          },
          error: (error) => {
            captureException(new Error('wbServerAckTimeoutSubject error'), {
              extra: {
                error: error,
              },
            });
          },
        });

      fromEvent(this.socket, SocketManagerEvent.RECONNECT)
        .pipe(untilDestroyed(this))
        .subscribe(this.onReconnect);

      fromEvent(this.socket, SocketManagerEvent.RECONNECT_ATTEMPT)
        .pipe(untilDestroyed(this))
        .subscribe(this.onReconnectAttempt);

      fromEvent(this.socket, SocketManagerEvent.RECONNECT_ERROR)
        .pipe(untilDestroyed(this))
        .subscribe(this.onReconnectError);

      fromEvent(this.socket, SocketManagerEvent.RECONNECT_FAILED)
        .pipe(untilDestroyed(this))
        .subscribe(this.onReconnectFailed);

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.SPACE_DOWNLOAD_START)
        .pipe(untilDestroyed(this))
        .subscribe(async (spaceDownloadStart) => {
          const _spaceDownloadStart = spaceDownloadStart as SpaceDownloadStart;
          const spaceSize = _spaceDownloadStart.spaceSize;
          const spaceId = _spaceDownloadStart.spaceId;
          this.telemetry.setSessionVars({ activeSessionSize: spaceSize });

          if (!this.socket) {
            return;
          }

          this.joinedRooms.push(spaceId);
          this.chunkedSpace = new ChunkedSpace(
            this.socket,
            spaceId,
            spaceSize,
            _spaceDownloadStart.session,
          );
          this.chunkedSpace
            .getSpaceDownloadProgressObservable()
            .pipe(untilDestroyed(this))
            .pipe(enterZone(this.zone))
            .subscribe((progress) => {
              this._spaceDownloadProgress$.next(progress);
            });
        });

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.SPACE_DOWNLOAD_URL)
        .pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          this.handleGcsSpaceDownload(data as GcsSpaceDownload);
        });

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.SPACE_CHUNK)
        .pipe(untilDestroyed(this))
        .subscribe((spaceChunk) => {
          this.zone.runOutsideAngular(() => {
            const _spaceChunk = spaceChunk as SpaceChunk;
            const chunk = _spaceChunk.chunk;
            const spaceId = _spaceChunk.spaceId;
            this.chunkedSpace.appendChunk(spaceId, chunk);
          });
        });

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.SPACE_DOWNLOAD_END)
        .pipe(untilDestroyed(this))
        .subscribe(async (spaceDownloadEnd) => {
          const _spaceDownloadEnd = spaceDownloadEnd as SpaceDownloadEnd;
          const sessionUpdate: SessionUpdate = _spaceDownloadEnd.spaceUpdate.sessionsUpdates[0];

          // Ensure that space is in the repo before setting synced
          await firstValueFrom(this.spaceRepo.getSpace$(sessionUpdate.sessionId).pipe(filterNil()));

          sessionUpdate.documentUpdate = {
            yjs_update: this.chunkedSpace.spaceBuffer,
          };
          this.applyYUpdates(_spaceDownloadEnd.spaceUpdate);
          this.spaceRepo.updateSpaceUI(sessionUpdate.sessionId, {
            hasSpaceSynced: true,
            isInitialSpaceStateLoaded: true,
          });

          this.sendSyncWithServer();
        });

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.SPACE_DOWNLOAD_ERROR)
        .pipe(untilDestroyed(this))
        .subscribe((spaceId) => {
          // rejoin in case of error
          this.joinSpaceRooms(this.populateSessionAuth([spaceId as string]) as SessionAuth[]);
        });

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.SYNC)
        .pipe(untilDestroyed(this))
        .subscribe((update) => {
          this.syncYUpdates(update as SessionsUpdate);
        });

      // Deliver immediately (do not buffer/throttle)
      fromEvent(this.socket, WebSocketEventType.JOIN_ROOMS_RES)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          const _res = res as { update: SessionsUpdate };
          const { update } = _res;

          const sessionId = update.sessionsUpdates?.[0].sessionId;
          if (!sessionId) {
            throw new Error(`Expected sessionId to be in update ${update}`);
          }

          this.joinedRooms.push(sessionId);
          this.applyYUpdates(update);
        });

      fromEvent(this.socket, SocketConnectionEvent.CONNECT)!
        .pipe(untilDestroyed(this))
        .subscribe(async () => {
          this.handleDisconnect();
          if (!this.socket) {
            return;
          }

          this.socketId = await this.socket.getSocketId();
          this._socketIdHistory.add(this.socketId);
          this.sharedDataService.socketReconnected.next();
          this.updateWebSocketConnectionStatus(ConnectionStatus.CONNECTED);

          // Emit connect event immediately
          this.wbSocketEventSubject.next(SocketEventType.CONNECT);
        });

      fromEvent(this.socket, SocketConnectionEvent.DISCONNECT)
        .pipe(untilDestroyed(this))
        .subscribe((reason) => {
          this.updateWebSocketConnectionStatus(ConnectionStatus.DISCONNECTED, false);
          this.disconnected = true;
          this._reconnectAttempt = 0;
          this.syncUserPresence('disconnected');

          // Emit disconnect event immediately
          this.wbSocketEventSubject.next(SocketEventType.DISCONNECT);

          // if the disconnection was initiated by the server or client, then reconnect manually
          if (reason === 'io server disconnect' || reason === 'io client disconnect') {
            this.connectWebSocket();
            // this.socket.connect();
          }
          this.telemetry.event('Socket disconnected', { reason });
        });

      fromEvent(this.socket, SocketConnectionEvent.CONNECT_ERROR)
        .pipe(untilDestroyed(this))
        .subscribe((error) => {
          modifiedSetTimeout(() => {
            this.connectWebSocket();
          }, 1000);
          this.telemetry.event('Socket connection error', { error });
        });

      // fromEvent(this.socket, 'currentSessionId')
      fromEvent(this.socket, WebSocketEventType.CURRENT_SESSION_ID)
        .pipe(untilDestroyed(this))
        .subscribe((id) => {
          this.analyticsService.setCurrentSessionId(id as string);
        });

      fromEvent(this.socket, WebSocketEventType.ANALYTICS_SESSION_ENDED_MANUALLY)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          this.analyticsService.handleManualEndSession(
            res as { newSessionId: string; oldSessionId: string },
          );
        });
    });
  }

  public subscribeToEvent(subscriber: SessionEventSubscriber, category: EventCategory): void {
    if (!this.eventRegistry[category]) {
      this.eventRegistry[category] = [subscriber];
    } else {
      this.eventRegistry[category].push(subscriber);
    }
  }

  public unSubscribeFromEvent(subscriber: SessionEventSubscriber, category: EventCategory): void {
    if (this.eventRegistry[category]) {
      this.eventRegistry[category] = this.eventRegistry[category].filter((s) => s !== subscriber);
    }
  }

  // Used for sendToBack and bringToFront fabric items in old versions
  public changeObjectStacking(uid: string, toFront: boolean): void {
    const [sessionId, frameUID] = this.getSessionData();

    if (!sessionId || !frameUID) {
      return;
    }

    const ySession = this.ySessions.get(sessionId);
    ySession.updateObjectsOrder(frameUID, uid, toFront);

    this.calcDiffAndSendUpdate(sessionId);

    return;
  }

  public sendEvent(sender: any, event: SessionEvent): void {
    if (sender === 'remote' || !this.user) {
      return;
    }

    this.publishEventToSubscribers(event, this.user._id);

    if (!this.getSessionId()) {
      return;
    }

    const sessionId = this.getSessionId();
    if (!sessionId) {
      return;
    }

    const update: SessionsUpdate = {
      userId: this.user._id,
      timestamp: new Date(),
      sessionsUpdates: [
        {
          sessionId: sessionId,
          events: [event],
        },
      ],
    };

    this.sendUpdateToServer('update', update);
  }

  private joinFrameRoom(spaceFrameID: string, breakOutRoomUid?: string) {
    if (
      !this.user ||
      !this.activeSessionId ||
      !(this.activeSessionId in this.sharedDataService.sessionAuthData)
    ) {
      return;
    }

    const authData = this.sharedDataService.sessionAuthData[this.activeSessionId].sessionAuth;
    const sessionAuth = { userId: this.user._id, ...authData };

    this.socket?.emit(
      WebSocketEventType.JOIN_FRAME_ROOM,
      sessionAuth,
      spaceFrameID,
      breakOutRoomUid,
    );
  }

  public leaveFrameRoom(): void {
    this.socket?.emit(WebSocketEventType.LEAVE_FRAME_ROOM);
  }

  /*
   * Joins a userRoom
   * A user can only subscribe to one user room at a time
   * This is an artificial restriction that can be extended in the future
   */
  public joinUserRoom(userId: string): void {
    if (
      !this.user ||
      !this.activeSessionId ||
      !(this.activeSessionId in this.sharedDataService.sessionAuthData)
    ) {
      return;
    }

    const authData = this.sharedDataService.sessionAuthData[this.activeSessionId].sessionAuth;
    const sessionAuth = { userId: this.user._id, ...authData };
    this.userRoom = userId;
    this.socket?.emit(WebSocketEventType.JOIN_USER_ROOM, sessionAuth, userId);
  }

  /*
   * Leaves a user room
   * We will no longer receive any user events for that user
   */
  public leaveUserRoom(userId?: string): void {
    // early return if we are already following a different user
    // since the WB endpoint will already leave the old room when following a new room.
    if (userId && userId !== this.userRoom) {
      return;
    }

    this.userRoom = undefined;
    this.socket?.emit(WebSocketEventType.LEAVE_USER_ROOM);
  }

  /*
   * Sends a userEvent to this users UserRoom
   * The backend enforces that you cannot send userEvents as different users
   */
  public sendUserEvent(event: UserEvent) {
    const userId = this.user?._id;
    if (!userId) {
      return;
    }

    event.userId = userId;
    this.socket?.emit(WebSocketEventType.USER_EVENT, event);
  }

  public setActiveSession(sessionId: string) {
    this.dismissGcsFailureModal();
    // currently we do the normal sync on first load even if the space is loaded before
    this.spaceRepo.updateSpaceUI(sessionId, {
      isInitialSpaceStateLoaded: false,
      hasSpaceSynced: false,
    });
    this.clearSyncState(sessionId);
    // make sure that we remove the ydoc as we do not want to do full sync with server on the first space load
    const ySession = this.ySessions.get(sessionId);
    ySession.getYDoc().destroy();
    this.ySessions.remove(sessionId);
    // Reset the cached YJS entity alongside the YDoc so the upcoming full sync rebuilds from a
    // clean slate; otherwise updateSpaceYjs merges the fresh snapshot onto a previous visit's
    // stale rooms/frames/canvasItems (leaking room state and board items across a re-entry).
    this.spaceRepo.resetSpaceYjs(sessionId);

    this.animationStartTime = new Date().getTime();
    this.sharedDataService.dataLoading.next(true);

    this.activeSessionId = sessionId;
    this.telemetry.setSessionVars({ activeSessionId: this.activeSessionId });
    this.spaceRepo.setActiveSpace(sessionId); // set active session in store
    this.joinRooms([this.activeSessionId]);
    this.createRTFrameSubscription();

    // Subscribe to get the first (non-null) value when it is available
    this.spaceRepo.activeSpace$.pipe(filterNotSynced(), first()).subscribe(() => {
      this.sharedDataService.dataLoading.next(false);
      this.logSpaceLoadingTimeToFS();
    });
  }

  /*
   * Tears down the connections and resets the data for the service
   */
  public stopService() {
    this.leaveRooms(this.joinedRooms);
    this.activeSessionId = undefined;

    this.rtFrameSubscription?.unsubscribe();
    this.sessionSubscriptions?.unsubscribe();

    this.cleanupSocket();
  }

  /*
   * Joins the rooms that are not already joined/cached and that have the authentication data
   *
   * If the session that is fetched from the socket server is the current session, that will be
   * emitted to the sharedDataService
   *
   * Otherwise it will be put into in inactive session list in the sharedDataService
   */
  public joinRooms(sessionIds: string[], callback?: () => void) {
    // syncing cached spaces with server
    this.syncSpaces(sessionIds);

    const joinableSessions = sessionIds.filter(
      (sessionId) =>
        this.joinedRooms.indexOf(sessionId) === -1 &&
        !this.spaceRepo.getSpace(sessionId)?.isInitialSpaceStateLoaded,
    );
    const sessionsWithAuth = this.populateSessionAuth(joinableSessions);
    if (!sessionsWithAuth?.length) {
      if (sessionIds.length > 0 && joinableSessions.length === 0) {
        if (callback) {
          callback();
        }
      }
      return;
    }
    this.joinSpaceRooms(sessionsWithAuth);
    this.getCurrentAnalyticsSession();
  }

  public leaveRooms(sessionIds: string[]) {
    this.socket?.emit(WebSocketEventType.LEAVE_ROOMS, sessionIds);
    const joinedRooms = this.joinedRooms.filter(
      (sessionId) => sessionIds.indexOf(sessionId) === -1,
    );
    this.joinedRooms = joinedRooms;
    sessionIds.forEach((sessionId) => {
      this.clearSyncState(sessionId);
    });
    if (this.activeSessionId && sessionIds.includes(this.activeSessionId)) {
      this.activeSessionId = undefined;
      this.dismissGcsFailureModal();
    }
  }

  public getMostRecentFrameIndex(frames: FrameObservableData[]): number {
    if (frames.length === 0) {
      return -1;
    }

    let lastUpdate = frames[0].updatedAt;
    let recentIndex = 0;
    frames.forEach((frame, index) => {
      if (frame.updatedAt && lastUpdate) {
        if (frame.updatedAt > lastUpdate) {
          lastUpdate = frame.updatedAt;
          recentIndex = index;
        }
      } else if (frame.updatedAt) {
        lastUpdate = frame.updatedAt;
        recentIndex = index;
      }
    });
    return recentIndex;
  }

  public undo(): boolean {
    return this.handleUndoRedo(UndoEnum.UNDO);
  }

  public redo(): boolean {
    return this.handleUndoRedo(UndoEnum.REDO);
  }

  public createYUpdate(documentUpdate: Uint8Array, sessionId?: string): SessionsUpdate | null {
    sessionId = sessionId ?? this.getSessionId();

    if (!sessionId || !this.user) {
      return null;
    }

    const sessionUpdate: SessionsUpdate = {
      userId: this.user._id,
      timestamp: new Date(),
      sessionsUpdates: [
        {
          sessionId,
          documentUpdate: {
            yjs_update: documentUpdate,
          },
        },
      ],
    };
    return sessionUpdate;
  }

  public sendMouseFrameEvent(
    type: string,
    events: (IEvent | undefined)[],
    metadata: any = null,
    volatile = false,
  ) {
    if (this.observerModeService.isObserverModeActive()) {
      return;
    }

    if (!metadata) {
      metadata = {};
    }

    metadata.canvasType = this.activeCanvasStateService.activeCanvasType;
    // Add screen share specific metadata
    this.addScreenShareAnnotationsMetadata(metadata);
    // Add uid for realtime scribbles
    if (type === MouseFrameEventType.DRAW) {
      metadata.uid = this.getRealtimeScribbleUid(metadata.state);
    }
    metadata.userName = this.user?.name;
    metadata.email = this.user?.email;

    const filteredEvents = <fabric.IEvent[]>events.filter((x) => x?.pointer);
    const mouse: Mouse[] = filteredEvents.map((e) => {
      const point = <Point>e.pointer;
      const modifiedPoint = this.modifyMetadataPointIfNeeded(metadata, point);
      const m: Mouse = {
        type: type,
        event: undefined,
        // Note: this cast is safe because we filter out all events without a point above
        point: modifiedPoint,
        metadata,
      };
      return m;
    });

    if (!mouse.length) {
      return;
    }

    const frameEvent: FrameEvent = { type: FrameEventType.Realtime, mouse };

    frameEvent.activeToolbar = this.getRTEventToolbar();

    // save the last mouse event to be sent for new joined users
    this.sharedDataService.lastMouseMoveEvent = frameEvent;
    this.sendFrameEvent(frameEvent, volatile);
  }

  private addScreenShareAnnotationsMetadata(metadata: any) {
    if (metadata.canvasType === ActiveCanvasType.GALLERY_VIEW) {
      metadata.tileId = this.activeCanvasStateService.activeTileId;
      const activeCanvas = this.activeCanvasStateService.activeCanvas;
      const canvasWidth = activeCanvas?.getWidth() ?? 0;
      const canvasHeight = activeCanvas?.getHeight() ?? 0;
      if (canvasWidth > 0 && canvasHeight > 0) {
        metadata.canvasWidth = canvasWidth;
        metadata.canvasHeight = canvasHeight;
      }
    }
  }

  // If the canvas type is screen share, normalize the point to the canvas size
  private modifyMetadataPointIfNeeded(metadata: any, point: Point) {
    const modifiedPoint =
      metadata.canvasType === ActiveCanvasType.GALLERY_VIEW &&
      metadata.canvasWidth &&
      metadata.canvasHeight
        ? {
            x: point.x / metadata.canvasWidth,
            y: point.y / metadata.canvasHeight,
          }
        : point;
    return modifiedPoint;
  }

  getRTEventToolbar(): string {
    const isLeaderMode = this.leaderModeRepositoryService.getCurrentState().leaderModeEnabled;

    const isBoardLocked = !this.sharedDataService.canModifySession();

    if (isBoardLocked && isLeaderMode) {
      return 'pointer';
    }

    if (this.sharedDataService.createComment$.getValue()) {
      return 'text';
    }

    // set to 'text' if there is an active text object
    const activeTextObject = this.sharedDataService.fabricCanvas
      ?.getActiveObjects()
      .find(
        (object) =>
          ['textbox', 'i-text'].includes(object.type as string) && (object as any).isEditing,
      );

    if (activeTextObject) {
      return 'text';
    }

    return this.mainToolbarService.rtEventToolbar;
  }

  /*
   * Send a frameEvent to the frameUID that this user is on
   */
  public sendFrameEvent(frameEvent: FrameEvent, volatile = false) {
    if (!volatile) {
      this.socket?.emit(WebSocketEventType.FRAME_EVENT, frameEvent);
    } else {
      this.socket?.volatile().emit(WebSocketEventType.FRAME_EVENT, frameEvent);
    }
  }

  /*
   * Adds a frame to the yjs document and propagates that change to all other clients
   */

  public addFrame(frame: Frame, sessionId: string, roomId: string) {
    frame = new Frame(frame);

    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }

    this.ySessions.get(sessionId).addFrame(roomId, frame);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /*
   * Adds multiple frames to the yjs document and propagates that change to all other clients
   */

  public addFrames(frames: Frame[], sessionId: string, roomId: string) {
    this.ySessions.get(sessionId).addFrames(roomId, frames);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /* er
   * Modifies a frame to the yjs document and propagates that change to all other clients
   */
  public modifyFrame(frame: Frame, sessionId: string, roomId: string) {
    frame = new Frame(frame);

    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }

    this.ySessions.get(sessionId).modifyFrame(roomId, frame);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * move a frame to a new position
   * data saved inside the Yjs document for the session
   * @param frame
   * @param fromIndex
   * @param toIndex
   * @param sessionId
   * @param roomId
   */
  public moveFrame(
    frame: Frame,
    fromIndex: number,
    toIndex: number,
    sessionId: string,
    roomId: string,
  ) {
    frame = new Frame(frame);
    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }
    const isFrameMoved = this.ySessions.get(sessionId).moveFrame(roomId, frame, fromIndex, toIndex);
    if (!isFrameMoved) {
      return;
    }
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * clear all the frame scribbles and highlights
   * @param frame
   * @param sessionId
   * @param roomId
   */
  public clearFrameAnnotations(frame: Frame, sessionId: string, roomId: string): void {
    frame = new Frame(frame);

    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }

    this.ySessions.get(sessionId).clearFrameAnnotations(roomId, frame.uid);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * clear all the frame content
   * @param frame
   * @param sessionId
   * @param roomId
   */
  public clearFrameContent(frame: Frame, sessionId: string, roomId: string): void {
    frame = new Frame(frame);

    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }

    this.ySessions.get(sessionId).clearFrameContent(roomId, frame.uid);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /*
   * Removes a frame to the yjs document and propagates that change to all other clients
   */
  public removeFrame(frame: Frame, sessionId: string, roomId: string) {
    frame = new Frame(frame);

    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }

    this.ySessions.get(sessionId).removeFrame(roomId, frame.uid);
    this.calcDiffAndSendUpdate(sessionId);
  }

  public updateFrameBoardFolder(frame: Frame, sessionId: string, roomId: string) {
    frame = new Frame(frame);

    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }

    this.ySessions.get(sessionId).updateFrameBoardFolder(roomId, frame);
    this.calcDiffAndSendUpdate(sessionId);
  }
  /**
   * update the frame privacy inside ySession
   * frame can be public => anyone in the space can access it
   * frame can be private => only hosts and  users in the privateUsers will be able to access it
   * @param frame
   * @param sessionId
   * @param roomId
   */
  public updateFramePrivacy(frame: Frame, sessionId: string, roomId: string) {
    frame = new Frame(frame);
    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }
    this.ySessions.get(sessionId).updateFramePrivacy(roomId, frame);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * will call the ySession to get the session and update the lock state for the frame
   * @param roomId
   * @param frame
   * @param sessionId
   */
  public updateFrameLockState(frame: Frame, sessionId: string, roomId: string): void {
    frame = new Frame(frame);
    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }
    this.ySessions.get(sessionId).updateFrameLockState(roomId, frame);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * will call the ySession to get the session
   * and update the background color and pattern for the frame
   * @param roomId
   * @param frame
   * @param sessionId
   */
  public updateFrameBackground(frame: Frame, sessionId: string, roomId: string): void {
    frame = new Frame(frame);
    if (!frame.validate()) {
      this.captureInvalidFrame(frame);
      return;
    }
    this.ySessions.get(sessionId).updateFrameBackground(roomId, frame);
    this.calcDiffAndSendUpdate(sessionId);
  }

  // Modifies the current users metadata for the current session
  public async modifyTemporaryUserMetadata(
    partialTemporaryMetadata: Partial<TemporaryUserMetadataEntryBase>,
  ) {
    const userId = this.user?._id;
    const sessionId = this.getSessionData()[0];

    if (!userId || !sessionId) {
      return;
    }

    if (!this.flagsService.isFlagEnabled(FLAGS.ENABLE_REDIS_USER_TEMPORARY_METADATA)) {
      this.modifyTemporaryUserMetadataYJS(partialTemporaryMetadata);
    } else {
      this.modifyTemporaryUserMetadataRedis(partialTemporaryMetadata);
    }
  }

  private async modifyTemporaryUserMetadataRedis(
    partialTemporaryMetadata: Partial<TemporaryUserMetadataEntryBase>,
  ) {
    const curState = this.temporaryUserMetadataRepository.getCurrentUserMetadata(
      this.userService.userUniqueHash,
      this.spaceRepo.activeSpaceId!,
    ) as TemporaryUserMetadataEntryRedis;

    const isUserStillInTheSameBreakoutRoom =
      curState.breakoutRoomUid === this.spaceRepo.activeSpaceCurrentRoomUid;

    if (partialTemporaryMetadata.leader === undefined) {
      partialTemporaryMetadata.leader = isUserStillInTheSameBreakoutRoom && curState.leader;
    }

    if (partialTemporaryMetadata.isBeingFollowed === undefined) {
      partialTemporaryMetadata.isBeingFollowed =
        isUserStillInTheSameBreakoutRoom && curState.isBeingFollowed;
    }

    if (partialTemporaryMetadata.isFollowing === undefined) {
      partialTemporaryMetadata.isFollowing =
        isUserStillInTheSameBreakoutRoom && curState.isFollowing;
    }

    // update temporary data only when
    // 1) leader mode is on
    // 2) leader mode changed
    // 3) raise hand changed
    // 4-5) user is being followed or stopped being followed
    // 6) user started / stopped following
    // 7) user ichanging the following tab
    if (
      curState.leader ||
      partialTemporaryMetadata.leader ||
      !!partialTemporaryMetadata.raiseHand !== !!curState.raiseHand ||
      curState.isBeingFollowed ||
      partialTemporaryMetadata.isBeingFollowed ||
      partialTemporaryMetadata.isFollowing !== curState.isFollowing ||
      (partialTemporaryMetadata.followingTabID &&
        curState.followingTabID &&
        partialTemporaryMetadata.followingTabID !== curState.followingTabID)
    ) {
      const dataEvent: TemporaryUserMetadataEntryRedis = {
        ...(isUserStillInTheSameBreakoutRoom ? curState : {}), // we only spread curr state if user didn't change BR
        ...partialTemporaryMetadata,
        userId: curState.userId,
        frameUID: this.spaceRepo.activeSpace?.selectedBoardUid,
        breakoutRoomUid: this.spaceRepo.activeSpaceCurrentRoomUid,
        sessionView: this.sharedDataService.sessionView.getSessionView(),
        spaceId: curState.spaceId,
        userUniqueHash: curState.userUniqueHash,
        timestamp: curState.timestamp + 1,
      };
      this.temporaryUserMetadataRepository.setNewEvents([dataEvent]);
      this.sendUpdatedUserMetadataToRedis();
    }
  }

  private modifyTemporaryUserMetadataYJS(
    partialTemporaryMetadata: Partial<TemporaryUserMetadataEntryBase>,
  ) {
    const sessionId = this.getSessionData()[0]!;
    const userId = this.user!._id;

    for (const socketId of this.socketIdHistory) {
      const yTemporaryUserMetadata = this.ySessions
        .get(sessionId)
        .getTemporaryUserSocketMetadata(socketId);
      if (
        !Object.prototype.hasOwnProperty.call(partialTemporaryMetadata, 'leader') &&
        yTemporaryUserMetadata?.leader
      ) {
        partialTemporaryMetadata.leader = yTemporaryUserMetadata.leader;
      }

      if (socketId !== this.socketId) {
        this.modifyYSession((ySession) => ySession.removeTemporaryUserMetadata(socketId));
        this.socketIdHistory.delete(socketId);
      }
    }

    // update temporary data only when
    // 1) leader mode is on
    // 2) leader mode changed
    // 3) raise hand changed
    if (
      partialTemporaryMetadata.leader ||
      this.ySessions.get(sessionId).getTemporaryUserSocketMetadata(this.socketId)?.leader ||
      partialTemporaryMetadata.raiseHand !==
        this.ySessions.get(sessionId).getTemporaryUserSocketMetadata(this.socketId)?.raiseHand
    ) {
      this.modifyYSession((ySession) =>
        ySession.modifyTemporaryUserMetadata(this.socketId, userId, {
          ...partialTemporaryMetadata,
          frameUID: this.spaceRepo.activeSpace?.selectedBoardUid,
          breakoutRoomUid: this.spaceRepo.activeSpaceCurrentRoomUid,
          sessionView: this.sharedDataService.sessionView.getSessionView(),
        }),
      );
    }
  }

  private sendUpdatedUserMetadataToRedis(capturedState?: TemporaryUserMetadataEntry) {
    this.numberOfUserMetadataUpdatesQueue++;
    // Read the state synchronously here (not inside the mutex): the mutex can defer the emit past
    // teardown's clearData()/resetActiveSpace(), when a re-read would miss the repo and return
    // default metadata for an undefined space instead of the update we're trying to send.
    const curState =
      capturedState ??
      this.temporaryUserMetadataRepository.getCurrentUserMetadata(
        this.userService.userUniqueHash,
        this.spaceRepo.activeSpaceId!,
      );
    this.sendUserMetadataUpdateMutex.runExclusive(() => {
      this.socket
        ?.timeout(this.WB_SERVER_ACK_TIMEOUT)
        .emit(WebSocketEventType.USER_TEMPORARY_METADATA_UPDATE, curState, (err: unknown) => {
          this.numberOfUserMetadataUpdatesQueue--;
          if (err) {
            this.telemetry.errorEvent('WB server update user metadata ack timeout');
            if (this.numberOfUserMetadataUpdatesQueue === 0) {
              // no other updates exist so we will try doing the update again with the same state
              // (re-reading here would hit the same stale-active-space problem on a leave).
              this.sendUserMetadataUpdateMutex.cancel();
              this.sendUpdatedUserMetadataToRedis(curState);
            }
          }
        });
    });
  }

  public modifySessionMetadata(sessionMetadata: Partial<SessionMetadata>) {
    this.modifyYSession((ySession) => ySession.modifySessionMetadata(sessionMetadata));
  }

  public async clearTemporaryUserMetadata(sessionId: string) {
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_REDIS_USER_TEMPORARY_METADATA)) {
      this.modifyTemporaryUserMetadata({ leader: false, raiseHand: false });
    } else {
      this.modifyYSession(
        (ySession) => ySession.clearTemporaryUserMetadata(this.socketId),
        sessionId,
      );
    }
  }

  public celebrate(celebrationId: number): void {
    const event = new SessionEvent(EventCategory.Users, 'users:celebrate', {
      celebrationId: celebrationId,
      breakoutRoomUid: this.spaceRepo.activeSpaceCurrentRoomUid,
    });
    this.sendEvent(this, event);
  }

  public emote(emojiId: number): void {
    const eventType =
      emojiId === emojiLibraryIndex.HAND_RAISED
        ? AnalyticsEventType.HAND_RAISED
        : AnalyticsEventType.REACTIONS;
    this.analyticsService.addToAnalyticsEventsBatch(eventType);
    const event = new SessionEvent(EventCategory.Users, 'users:emote', { emojiId: emojiId });
    this.sendEvent(this, event);

    // to handle spamming of emotes store timerIds
    this.timerArray.push(
      modifiedSetTimeout(() => {
        const resetEvent = new SessionEvent(EventCategory.Users, 'users:emote', { emojiId: -1 });
        this.sendEvent(this, resetEvent);
        this.timerArray = [];
      }, 2500),
    );

    if (this.prevEmojiId >= 0 && this.timerArray.length - 1 > 0) {
      // clear older timeouts
      for (let i = 0; i < this.timerArray.length - 1; i++) {
        clearTimeout(this.timerArray[i]);
      }
    }
    this.prevEmojiId = emojiId;
  }

  /**
   * send the pointers of the user to be emitted to the new joined user
   * @param frameEvent
   * @private
   */
  private sendMousePointerEvent(frameEvent: FrameEvent): void {
    this.socket?.volatile().emit(WebSocketEventType.POINTER_USER_EVENT, frameEvent);
    // this.socket.volatile.emit('pointerUserEvent', frameEvent);
  }

  public isConvertedToRooms(sessionId?: string): boolean {
    if (!sessionId) {
      return false;
    }
    return this.ySessions.get(sessionId).isConvertedToRooms();
  }

  /**
   * Resets all boards and folders for a room - removes all board folders,
   * keeps the first frame (index 0) renamed as "Board 1" with cleared content, and removes all other frames
   * Everything happens in a single transaction for optimal performance
   */
  resetRoomBoardsAndFolders(): void {
    const sessionId = this.getSessionId();
    const currentRoomUid = this.spaceRepo.activeSpace?.currentRoomUid;
    if (!sessionId || !currentRoomUid) {
      return;
    }
    const ySession = this.ySessions.get(sessionId);

    // Use the new consolidated method to reset everything in one transaction
    ySession.resetRoomBoardsAndFolders(
      currentRoomUid,
      `${this.translateService.instant('Board')} 1`,
    );

    this.calcDiffAndSendUpdate(sessionId);
  }

  /* Add the newly created breakout room to yjs doc and propagates the updates to other clients */
  addRooms(sessionId: string, rooms: Room[]) {
    rooms.forEach((room) => this.validateRoomSchema(room));
    this.ySessions.get(sessionId).addRooms(rooms);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * add new board folder to the yjs doc board folders array
   * @param sessionId
   * @param boardFolder
   */
  addBoardFolder(sessionId: string, boardFolder: BoardFolder): void {
    this.ySessions.get(sessionId).addBoardFolder(boardFolder);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /**
   * remove the board folder with its frames
   * @param boardFolder
   * @param sessionId
   * @param roomId
   */
  removeBoardFolder(boardFolder: BoardFolder, sessionId: string, roomId: string) {
    if (!sessionId || !roomId) {
      return;
    }
    const ySession = this.ySessions.get(sessionId);

    // Use the updated method to handle everything within YSession
    ySession.removeBoardFolderWithFrames(roomId, boardFolder);

    this.calcDiffAndSendUpdate(sessionId);
  }

  /* er
   * Modifies a boardFolder to the yjs document and propagates that change to all other clients
   */
  public modifyBoardFolder(boardFolder: BoardFolder, sessionId: string, roomId: string) {
    boardFolder = new BoardFolder(boardFolder);

    if (!sessionId || !roomId) {
      return;
    }

    this.ySessions.get(sessionId).modifyBoardFolder(boardFolder);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /* Modify a breakout room in yjs doc and propagates the updates to other clients*/
  public modifyRoom(room: Room, sessionId: string) {
    room = new Room(room);
    this.validateRoomSchema(room);
    this.ySessions.get(sessionId).modifyRoom(room);
    this.calcDiffAndSendUpdate(sessionId);
  }

  public moveFrameBetweenRooms(sessionId: string, movedBoardsPlacement: MovedBoardsPlacement[]) {
    this.ySessions.get(sessionId).moveFrameBetweenRooms(movedBoardsPlacement);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /* Delete breakout room from yjs doc and propagates the updates to other clients */
  deleteRooms(sessionId: string, roomIds: string[]) {
    roomIds = roomIds.filter((roomId) => Session.getMainRoomId(sessionId, true) !== roomId);

    this.ySessions.get(sessionId).deleteRooms(roomIds);
    this.calcDiffAndSendUpdate(sessionId);
  }

  /*
   * Returns the list of authData structures to pass to the socket server to get the session data
   */
  public populateSessionAuth(sessionIds: string[]) {
    if (!this.user) {
      return;
    }
    const res: { id: string; token: string; userId: string }[] = [];
    for (const sessionId of sessionIds) {
      const sessionData = this.sharedDataService.sessionAuthData;
      if (!(sessionId in sessionData)) {
        continue;
      }
      const sessionAuth = sessionData[sessionId].sessionAuth;
      res.push({
        id: sessionId,
        token: sessionAuth.token,
        userId: this.user._id,
      });
    }
    return res;
  }

  setupSessionListeners() {
    // setupSessionListeners() runs on every in-place space switch (the session is reused), so
    // dispose the previous set before rebuilding to avoid leaking these subscriptions per switch.
    this.sessionSubscriptions?.unsubscribe();

    // listen for frame or screen share tile changes and emit undo/redo stack length for each undo
    let undoStackSubscriber: Subscription | undefined;
    let yDocPendingChangesSubscriber: Subscription | undefined;
    this.sessionSubscriptions = combineLatest([
      this.activeCanvasStateService.activeCanvasType$,
      this.spaceRepo.activeSpaceId$,
      this.spaceRepo.activeSpaceSelectedBoardUid$,
      this.activeCanvasStateService.activeTileId$,
    ]).subscribe(([, spaceId]) => {
      const syncChannelData = this.getSyncChannelData();
      if (!spaceId || !syncChannelData) {
        this.undoStackSubject.next(0);
        this.redoStackSubject.next(0);
        return;
      }
      const { syncChannelId, boardUid } = syncChannelData;

      if (!boardUid) {
        this.undoStackSubject.next(0);
        this.redoStackSubject.next(0);
        return;
      }

      const ySession = this.ySessions.get(syncChannelId);
      if (!ySession) {
        this.undoStackSubject.next(0);
        this.redoStackSubject.next(0);
        return;
      }

      undoStackSubscriber?.unsubscribe();
      undoStackSubscriber = ySession.getUndoStackObservable(boardUid).subscribe((stacks) => {
        this.undoStackSubject.next(stacks.undoStack);
        this.redoStackSubject.next(stacks.redoStack);
      });
      this.sessionSubscriptions?.add(undoStackSubscriber);

      yDocPendingChangesSubscriber = this.observerUtils
        .throttledObservableOf(
          this.ySessions.get(spaceId).yDocPendingChangesSubject,
          this.YDOC_PENDING_CHANGES_DELAY,
        )
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (next) => {
            if (next) {
              this.handleYDocPendingChages(next);
            }
          },
          error: (error) => {
            captureException(new Error('yDocPendingChangesSubject error'), {
              extra: {
                error: error,
              },
            });
          },
        });
      this.sessionSubscriptions?.add(yDocPendingChangesSubscriber);
      const pendingStructsSyncReachedThresholdSubscriber = this.ySessions
        .get(spaceId)
        .pendingStructsSyncReachedThreshold$.pipe(untilDestroyed(this), take(1))
        .subscribe(() => {
          this.telemetry.event('YJS Missing Update Sync Reached Max Threshold', {
            spaceId: this.activeSessionId,
          });
        });
      this.sessionSubscriptions?.add(pendingStructsSyncReachedThresholdSubscriber);
    });

    // Reset undo/redo stacks when a frame's content/annotations are cleared. Registered once per
    // setup and bound to sessionSubscriptions (instead of per-emission) so they don't accumulate.
    this.sessionSubscriptions.add(
      this.sharedDataService.clearFrameContent$.subscribe(() => {
        this.undoStackSubject.next(0);
        this.redoStackSubject.next(0);
      }),
    );
    this.sessionSubscriptions.add(
      this.sharedDataService.clearFrameAnnotations$.subscribe(() => {
        this.undoStackSubject.next(0);
        this.redoStackSubject.next(0);
      }),
    );
  }

  private validateRoomSchema(room: Room): void {
    const result = permissionsSchema.safeParse(room.permissions);
    if (!result.success) {
      captureException(new Error('Room Permission Validation Error'));
      this.telemetry.errorEvent('Room Permission Validation Error', { room, result });
    }
  }

  private handleYDocPendingChages(missingStructs: Map<number, number>) {
    this.telemetry.event('YJS Missing Update', {
      user: this.user?._id,
      missingStructs: JSON.stringify(Object.fromEntries(missingStructs)),
      spaceId: this.activeSessionId,
    });
    this.sendSyncWithServer();
  }

  private handleDisconnect() {
    if (this.disconnected) {
      const oldRooms = [...this.joinedRooms];
      this.joinedRooms = [];

      // Once we join again we should publish any updates we made in the interim
      this.joinRooms(oldRooms);

      if (this.userRoom) {
        this.joinUserRoom(this.userRoom);
      }
    }
  }

  private cleanupSocket() {
    this.socket?.disconnect();
  }

  /*
   * Broadcasts an event to all remote users that have joined the session
   */

  private sendHeartbeat() {
    if (!this.user) {
      return;
    }
    this.socket?.emit(WebSocketEventType.HEARTBEAT, this.activeSessionId);
  }

  private createRTFrameSubscription() {
    if (this.rtFrameSubscription) {
      this.rtFrameSubscription.unsubscribe();
    }

    if (!this.socket) {
      throw new Error('creating rt subcsription before socket has been created');
    }

    this.roomsSubscription?.unsubscribe();
    // Listen to changes in activeSpaceId, selectedBoard, currentRoom and activeCanvasType/activeTileId and:
    // 1. Call join room when a board/room combination changes
    // OR
    // when activeCanvasType is screen share and activeTileId changes
    // 2. When activeSpaceId/currentRoom changes, "listen" to frame changes to update Space store
    this.rtFrameSubscription = combineLatest([
      this.spaceRepo.activeSpaceId$,
      this.spaceRepo.activeSpaceSelectedBoardUid$,
      this.spaceRepo.activeSpaceCurrentRoomUid$,
      this.activeCanvasStateService.activeCanvasType$,
      this.activeCanvasStateService.activeTileId$,
      fromEvent(this.socket, SocketConnectionEvent.CONNECT).pipe(startWith({})),
    ]).subscribe(([spaceId, selectedBoardUid, currentRoomUid, activeCanvasType, activeTileId]) => {
      if (!spaceId || !currentRoomUid) {
        return;
      }

      const frameUid =
        activeCanvasType === ActiveCanvasType.GALLERY_VIEW ? activeTileId : selectedBoardUid;

      if (!frameUid) {
        return;
      }

      this.joinFrameRoom(`${spaceId}_${frameUid}`, currentRoomUid);
    });
  }

  private getSessionId() {
    return this.spaceRepo.activeSpace?._id;
  }

  private handleBulkFrameEvents(events: FrameEvent[]) {
    const realTimeEvents = events.filter(
      (event) => event.userId && event.type === FrameEventType.Realtime,
    );
    const viewportEvents = events.filter(
      (event) => event.userId && event.type === FrameEventType.Viewport && (event as any).location,
    );
    const userEvents = events.filter(
      (event) =>
        event.userId &&
        (event.type === FrameEventType.UserLeft ||
          event.type === FrameEventType.UserJoined ||
          event.type === FrameEventType.RequestFrame),
    );

    // handle RealTime events in bulk
    const bulkEvents: { mouse: Mouse; user: string; activeToolbar?: string }[] = [];
    for (const realtimeEvent of realTimeEvents) {
      realtimeEvent?.mouse?.forEach((m) => {
        if (m) {
          m.metadata.timestamp = performance.now(); // add timestamp when event was received
          bulkEvents.push({
            mouse: m,
            user: realtimeEvent.userId!,
            activeToolbar: realtimeEvent.activeToolbar,
          });
        }
      });
    }

    if (bulkEvents.length) {
      this.realTimeEventSubject.next(bulkEvents);
    }

    // handle User events next
    this.handleUserEvents(userEvents);

    // handle viewport events next
    for (const vpEvent of viewportEvents) {
      const loc = (vpEvent as any).location as UserLocation;
      if (loc) {
        this.viewportEventSubject.next({
          user: vpEvent.userId!,
          location: loc,
          userName: vpEvent.userName,
        });
      }
    }
  }

  private handleUserEvents(userEvents: FrameEvent[]) {
    const usersLeft: string[] = [];
    const usersJoined: string[] = [];
    for (const userEvent of userEvents) {
      if (userEvent.type === FrameEventType.UserLeft) {
        usersLeft.push(userEvent.userId!);
      } else if (userEvent.type === FrameEventType.UserJoined) {
        usersJoined.push(userEvent.userId!);
        // send the current mouse pointers to the user joined, so we can draw the mouses
        if (this.flagsService.isFlagEnabled(FLAGS.PROPAGATE_POINTER_LOCATIONS_ON_JOIN)) {
          this.sendMousePointerEvent({
            ...this.sharedDataService.lastMouseMoveEvent,
            receiverSocketId: userEvent.receiverSocketId,
          });
        }
      } else if (userEvent.type === FrameEventType.RequestFrame) {
        this.sendMousePointerEvent({
          ...this.sharedDataService.lastMouseMoveEvent,
          receiverSocketId: userEvent.receiverSocketId,
        });
      }
    }
    if (usersLeft.length) {
      this.usersLeftFrame.next(usersLeft);
    }
    if (usersJoined.length) {
      this.usersJoinedFrame.next(usersJoined);
    }
  }

  private applyUpdate(update: SessionsUpdate) {
    this.applyYUpdates(update);
    this.applyEventUpdates(update);
  }

  private applyBulkUpdates(updates: SessionsUpdate[]) {
    for (const update of updates) {
      this.applyUpdate(update);
    }
  }

  /*
   * Applies the documentUpdate for all frames, then rerenders the current frame
   */
  private applyYUpdates(update: SessionsUpdate) {
    this.logYMetrics(update);
    update.sessionsUpdates.forEach((sessionUpdate) => {
      try {
        if (sessionUpdate.documentUpdate && sessionUpdate.documentUpdate.yjs_update) {
          const ySession = this.ySessions.get(sessionUpdate.sessionId);
          ySession.applyUpdate(sessionUpdate, SERVER_ORIGIN);
          this.updateServerState(sessionUpdate);
        }
      } catch (err) {
        captureException(err);
        this.telemetry.errorEvent('corrupted_update');
      }
    });
    this.sharedDataService.socketYUpdate.next();
  }

  private logYMetrics(update: SessionsUpdate) {
    const e2eLatency = new Date().getTime() - new Date(update.timestamp).getTime();
    this.telemetry.updateMetric(ExternalMetric.yjsUpdateLatency, e2eLatency);

    update.sessionsUpdates
      .map((sessionUpdate) => sessionUpdate.documentUpdate?.yjs_update?.byteLength)
      .filter(notEmpty)
      .forEach((updateSize) => {
        this.telemetry.updateMetric(ExternalMetric.yjsUpdateSize, updateSize);
        this.telemetry.incrementMetric(CountedMetric.yDocRx);
      });
  }

  private _applyUpdateAndRemovePendingUpdates(update: SessionUpdate) {
    const ySession = this.ySessions.get(update.sessionId);
    const pendingUpdatesRemoved = ySession.applyUpdateAndRemovePendingUpdates(
      update,
      SERVER_ORIGIN,
    );
    if (pendingUpdatesRemoved) {
      this.telemetry.event('space has pending updates on load');
    }
  }

  private updateServerState(update: SessionUpdate | ServerStateUpdate) {
    const sessionId = update.sessionId;
    if (update.serverStateVector) {
      this.ySessions.get(sessionId).updateServerState(update.serverStateVector);
    }
  }

  private updateBulkServerStates(updates: SessionUpdate[] | ServerStateUpdate[]) {
    for (const update of updates) {
      this.updateServerState(update);
    }
  }

  /*
   * Sends the update from the remote client to the local client
   */
  private applyEventUpdates(update: SessionsUpdate) {
    update.sessionsUpdates.forEach((sessionUpdate) => {
      // Don't want to update the UI if it is for a different session
      if (sessionUpdate.sessionId !== this.activeSessionId) {
        return;
      }

      if (!sessionUpdate.events) {
        return;
      }
      sessionUpdate.events.forEach((event) => {
        this.publishEventToSubscribers(event, update.userId);
      });
    });
  }

  private publishEventToSubscribers(event: SessionEvent, userId: string) {
    // Update any local subscribers
    if (!this.eventRegistry[event.category]) {
      return;
    }

    this.zone.run(() => {
      this.eventRegistry[event.category].forEach((subscriber: SessionEventSubscriber) => {
        subscriber.sessionEventReceived(event, userId);
      });
    });
  }

  private getCurrentFrameUID(): string | undefined {
    return this.spaceRepo.activeSpace?.selectedBoardUid;
  }

  private getSessionData(sessionId?: string, frameUID?: string) {
    sessionId = sessionId || this.getSessionId();
    frameUID = frameUID || this.getCurrentFrameUID();

    return [sessionId, frameUID];
  }

  // Response is true if an undo/redo occurred,
  // false if you are at the end of the undo/redo stack or data is missing
  private handleUndoRedo(type: UndoEnum): boolean {
    const syncChannelData = this.getSyncChannelData();
    if (!syncChannelData) {
      return false;
    }
    const { syncChannelId, boardUid } = syncChannelData;

    const ySession = this.ySessions.get(syncChannelId);
    const undoRes = ySession.undoRedo(boardUid, type);
    // If no objects have changed then there was no op
    if (!undoRes) {
      return false;
    }

    this.calcDiffAndSendUpdate(syncChannelId);

    return true;
  }

  private getSyncChannelData(): { syncChannelId: string; boardUid: string } | undefined {
    const activeSpace = this.spaceRepo.activeSpace;
    if (!activeSpace) {
      return undefined;
    }

    let syncChannelId: string;
    let boardUid: string;

    if (this.activeCanvasStateService.activeCanvasType === ActiveCanvasType.GALLERY_VIEW) {
      syncChannelId = this.activeCanvasStateService.getScreenShareAnnotationsRoomId();
      boardUid = this.activeCanvasStateService.activeTileId;
      return { syncChannelId, boardUid };
    }

    if (!activeSpace.selectedBoardUid) {
      return undefined;
    }

    syncChannelId = activeSpace._id;
    boardUid = activeSpace.selectedBoardUid;
    return { syncChannelId, boardUid };
  }

  private getRealtimeScribbleUid(state: string) {
    // For realtime drawing, create and remember the uids for each scribble object.
    // This will be used to co-relate them to their corresponding Fabric objects,
    // so the remote clients can swap out realtime scribbles with persistent ones as soon
    // as they're received and rendered for them.
    let currentScribbleUid;
    if (state === 'start') {
      currentScribbleUid = uuidv4();
      this.pendingYDocScribbleUids.push(currentScribbleUid);
    } else {
      currentScribbleUid = this.pendingYDocScribbleUids[this.pendingYDocScribbleUids.length - 1];
    }
    return currentScribbleUid;
  }

  public getFabricScribbleUid(scribbleType: ScribbleType) {
    // For draw and highlight scribbles, use the same uids that were sent for their realtime strokes
    let uid;
    if (
      [ScribbleType.DRAW, ScribbleType.HIGHLIGHT].includes(scribbleType) &&
      this.pendingYDocScribbleUids.length
    ) {
      uid = this.pendingYDocScribbleUids.shift(); // remove the first element
    } else {
      uid = uuidv4();
    }
    return uid;
  }

  private async captureInvalidFrame(frame: Frame) {
    console.log('Sending invalid Update');
    if (!frame) {
      return;
    }
    const error = {
      frame: frame,
      stackTrace: Error().stack,
    };
    const jsn = JSON.stringify(error);

    const blob = new Blob([jsn], { type: 'application/json' });
    const file = new File([blob], 'sessionLogs.json');

    this.uploadService.uploadToFireStorage(file, (url) => {
      captureException(new Error('Invalid frame detected'), {
        extra: {
          logsUrl: url,
        },
      });
    });
  }

  /*
   * Modifies a ySession and emits an update to all other users in the session
   * callback: a function that takes in a ySession and produces a yjs update
   */
  private modifyYSession(callback: (ySession: YSession) => void, sessionId?: string) {
    if (!sessionId) {
      sessionId = this.getSessionData()[0];
    }

    if (!sessionId) {
      return;
    }

    const ySession = this.ySessions.get(sessionId);
    callback(ySession);
    this.calcDiffAndSendUpdate(sessionId);
  }

  private handleConnectionStatus(): void {
    this.networkService.wbServer.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value === ConnectionStatus.DISCONNECTED) {
        this.setIsOfflineIndicator(true);
      } else if (value === ConnectionStatus.CONNECTED) {
        this.setIsOfflineIndicator(false);
      }
    });
    this.networkService.browserIndicator.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value === ConnectionStatus.DISCONNECTED) {
        this.setIsOfflineIndicator(true);
      } else if (value === ConnectionStatus.CONNECTED) {
        if (this.networkService.wbServer.value) {
          this.setIsOfflineIndicator(false);
        }
      }
    });
  }

  private setIsOfflineIndicator(newValue: boolean): void {
    // Check if the state is changing before entering critical section below (to avoid hammering on the mutex)
    if (this._isOffline === newValue) {
      return;
    }

    this.changeOfflineStateMutex.runExclusive(() => {
      // check again if the state is about to change, otherwise do nothing.
      if (this._isOffline === newValue) {
        return;
      }

      if (!newValue) {
        // back online
        this._reconnectAttempt = 0;
        this.networkStatus.next(NetworkStatus.CONNECTED);
        this.showReconnectedToServerNotification();
      }

      this._isOffline = newValue;
    });
  }

  isOfflineMode(): Promise<boolean> {
    return this.changeOfflineStateMutex.runExclusive(() => this.isOffline);
  }

  /**
   * build and show the notification when there is a connection issue
   * @private
   */
  private showReconnectingToServerNotification(): void {
    this.notificationToasterService.dismissNotificationsByCode([SUCCESSES.RECONNECTED_TO_SERVER]);
    this.notificationToasterService.showLoadingNotification(
      this.translateService.instant('Connection lost. Reconnecting...'),
      ERRORS.RECONNECTING_TO_SERVER,
      false, // autoDismiss
      true, // isError
    );
    this.telemetry.event('Reconnecting to server notification', {});
  }
  private showReconnectedToServerNotification() {
    this.notificationToasterService.dismissLoadingNotification(ERRORS.RECONNECTING_TO_SERVER);
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant("Reconnected. You're back online!"),
      SUCCESSES.RECONNECTED_TO_SERVER,
    );
    this.telemetry.event('Connected to server notification', {});
  }

  private sendEmote(gestureName: GestureName) {
    this.emote(emojiLibraryIndex[gestureName]);
  }

  private skipUpdatesWithSyncInProgress(update: SessionsUpdate | null): SessionsUpdate | null {
    if (!this.flagsService.isFlagEnabled(FLAGS.BLOCK_UPDATES_DURING_SYNC)) {
      return update;
    }
    if (update?.sessionsUpdates) {
      update.sessionsUpdates = update.sessionsUpdates.filter(
        (sessionUpdate) =>
          !sessionUpdate.documentUpdate || !this.syncsInProgress.has(sessionUpdate.sessionId),
      );
    }
    return update;
  }

  public sendUpdateToServer(event: any, update: SessionsUpdate | null) {
    // do not send updates for sessions that has sync in progress
    update = this.skipUpdatesWithSyncInProgress(update);

    if (!update?.sessionsUpdates?.length) {
      return;
    }
    // skipping ack timeout for non-update events like network
    if (update?.sessionsUpdates[0].documentUpdate) {
      this.socket?.timeout(this.WB_SERVER_ACK_TIMEOUT).emit(
        event,
        update,
        (
          err: unknown,
          response: {
            serverStateVector: SessionUpdate | ServerStateUpdate;
            err: WhiteboardServerErrors;
          },
        ) => {
          if (err) {
            this.telemetry.event('WB server update ack timeout');
            this.wbServerAckTimeoutSubject.next(true);
          } else if (response.err === WhiteboardServerErrors.UPDATE_FAILED) {
            // Re-sync the session whose update the server rejected
            this.telemetry.event('Sync With Server Failed');
            this.sendSyncWithServer(update?.sessionsUpdates[0].sessionId);
          } else if (response.serverStateVector) {
            const { serverStateVector } = response;
            this.updateServerState(serverStateVector);
            this.wbServerAckTimeoutSubject.next(false);
          }
        },
      );
      // increment yDoc send
      this.telemetry.incrementMetric(CountedMetric.yDocTx);
    } else {
      this.socket?.emit(event, update);
    }
    const allUpdates = update?.sessionsUpdates.map(
      (sessionUpdate) => sessionUpdate.documentUpdate?.yjs_update,
    );
    if (allUpdates) {
      for (const sentUpdate of allUpdates) {
        // Check if update is more than 100 KB
        if (sentUpdate && sentUpdate.byteLength > 102400) {
          this.telemetry.event('ydoc_large_update', {
            ydoc_update_size: sentUpdate.byteLength / 1024,
            spaceId: this.spaceRepo.activeSpace?._id,
            userId: this.user?._id,
          });
        }
      }
    }
  }

  private syncYUpdates(syncFromServer: SessionsUpdate) {
    syncFromServer.sessionsUpdates.forEach((sessionUpdate) => {
      this.clearSyncState(sessionUpdate.sessionId);
      const ySession = this.ySessions.get(sessionUpdate.sessionId);
      ySession.applyUpdate(sessionUpdate, SERVER_ORIGIN);

      this.updateServerState(sessionUpdate);
      const update = ySession.getUpdate();
      if (update && !sessionUpdate.isFirstSpaceSync) {
        this.sendUpdateToServer('update', this.createYUpdate(update, sessionUpdate.sessionId));
      }

      const space = this.spaceRepo.getSpace(sessionUpdate.sessionId);
      if (space && !space.hasSpaceSynced) {
        const firstSpaceSyncSize = sessionUpdate?.documentUpdate?.yjs_update?.byteLength ?? 0;
        const initialGCSSpaceSize = space.initialGCSSpaceSize ?? 0;
        this.telemetry.setSessionVars({
          activeSessionSize: firstSpaceSyncSize + initialGCSSpaceSize,
        });
        this.spaceRepo.updateSpaceUI(sessionUpdate.sessionId, { hasSpaceSynced: true });
      }
    });
    this.sharedDataService.socketYUpdate.next();
  }

  private sendSyncWithServer(
    sessionId?: string,
    forceReSync?: boolean,
    isFirstSpaceSync?: boolean,
  ) {
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_SYNC_WITH_WB_SERVER)) {
      sessionId = sessionId || this.getSessionId();
      // skip the check of getSpace from spaceRepo for screen share annotations sessionId since it is not a space
      if (
        (!sessionId ||
          this.syncsInProgress.has(sessionId) ||
          (!isScreenShareAnnotationsSpaceID(sessionId) &&
            !this.spaceRepo.getSpace(sessionId)?.isInitialSpaceStateLoaded)) &&
        (!forceReSync || !sessionId)
      ) {
        return;
      }
      const sessionSync: SessionsUpdate = {
        userId: this.user!._id,
        timestamp: new Date(),
        sessionsUpdates: [
          {
            sessionId: sessionId!,
            documentUpdate: {
              yjs_update: undefined,
            },
            clientStateVector: this.ySessions.get(sessionId).getClientStateVector(),
            isFirstSpaceSync: isFirstSpaceSync,
          },
        ],
        skipFullSyncThresholdOnFirstLoad:
          this.constantMemoryApiSpaceSyncSettingsService.skipFullSyncThresholdOnFirstLoad,
      };
      this.syncsInProgress.add(sessionId, this.SYNC_TIMEOUT);
      // Schedule a re-sync if the initial sync did not complete within SYNC_TIMEOUT
      this.scheduleResyncOnTimeout(sessionId);
      this.addSessionIdIfNotExists(sessionId);
      this.socket?.emit(WebSocketEventType.SYNC_WITH_SERVER, sessionSync);
    }
  }

  // add session id to joined rooms if it is not already in the list
  // currently used to add screen share annotations session id
  private addSessionIdIfNotExists(sessionId: string): void {
    if (!this.joinedRooms.includes(sessionId)) {
      this.joinedRooms.push(sessionId);
    }
  }

  private async syncSessionData(spaceId: string) {
    try {
      const spaceFromServer: Session | undefined = (
        await this.spacesService.getSession(spaceId).toPromise()
      )?.session;
      const cachedSpace = this.spaceRepo.activeSpace;
      if (!spaceFromServer || !cachedSpace) {
        return;
      }
      cachedSpace.populatedUsers = spaceFromServer.populatedUsers;
      cachedSpace.title = spaceFromServer.title;
      cachedSpace.isLocked = spaceFromServer.isLocked;
      cachedSpace.visibility = spaceFromServer.visibility;
      cachedSpace.sessionPermissions = spaceFromServer.sessionPermissions;
      cachedSpace.users = spaceFromServer.users;
      cachedSpace.users
        .filter((user) => !Session.isOwnedByUser(spaceFromServer, user._id))
        .forEach((user) => (user.userPermissions = spaceFromServer.sessionPermissions));
      cachedSpace.accessRequesters = spaceFromServer.accessRequesters;
      cachedSpace.populatedAccessRequesters = spaceFromServer.populatedAccessRequesters;
      cachedSpace.settings = spaceFromServer.settings;

      this.spaceRepo.updateSpace(spaceId, cachedSpace);
    } catch (err) {
      console.log(err);
    }
  }

  public callSyncSessionData(): void {
    this._syncSessionDataSubject.next();
  }

  private updateWebSocketConnectionStatus(
    wsConnectionstatus: ConnectionStatus.CONNECTED | ConnectionStatus.DISCONNECTED,
    shouldUpdateWBStatus = true,
  ) {
    if (shouldUpdateWBStatus) {
      this.networkService.wbServer.next(wsConnectionstatus);
    }
    this.disconnected = wsConnectionstatus == ConnectionStatus.DISCONNECTED;
  }

  getSocketId() {
    return this.socketId;
  }

  private async syncSpaces(spaceIds: string[]) {
    // syncing with server should reconnect user to room
    spaceIds
      .filter(
        (spaceId) =>
          this.spaceRepo.getSpace(spaceId)?.isInitialSpaceStateLoaded ||
          isScreenShareAnnotationsSpaceID(spaceId),
      )
      .forEach(async (spaceId) => {
        this.sendSyncWithServer(spaceId);
        if (isScreenShareAnnotationsSpaceID(spaceId)) {
          // no need to sync session data for screen share annotations
          return;
        }
        await this.syncSessionData(spaceId);
      });
  }

  private joinSpaceRooms(sessionsWithAuth: SessionAuth[]) {
    this.socket?.emit(WebSocketEventType.JOIN_SOCKET_ROOMS, {
      sessionsWithAuth,
      chunking: this.flagsService.isFlagEnabled(FLAGS.SPACE_BINARY_CHUNKING),
      breakoutRoomsEnabled: this.flagsService.isFlagEnabled(FLAGS.BREAKOUT_ROOMS),
      enableDirectClientDownloadFromGCS: this.flagsService.isFlagEnabled(
        FLAGS.ENABLE_DIRECT_CLIENT_DOWNLOAD_FROM_GCS,
      ),
      shouldSendGcsDownloadLinkImmediately: this.flagsService.isFlagEnabled(
        FLAGS.ENABLE_DIRECT_CLIENT_DOWNLOAD_FROM_GCS,
      ),
    });
  }

  public getSpaceDownloadProgressObservable(): Observable<number> {
    return this._spaceDownloadProgress$.asObservable();
  }

  public sessionToJSON(sessionId: string) {
    return this.ySessions.get(sessionId).getYDocAsJSON();
  }

  public sessionToBase64(sessionId: string) {
    return this.ySessions.get(sessionId).getYDocAsBase64();
  }

  public getSessionSize(sessionId: string) {
    const byte = this.ySessions.get(sessionId).getYDocSize();
    return {
      sizeByte: `${byte} byte`,
      sizeKB: `${(byte * 0.001).toFixed(2)} kb`,
      sizeMB: `${(byte * 0.000001).toFixed(2)} mb`,
    };
  }

  public logSize() {
    if (this.activeSessionId) {
      const size = this.getSessionSize(this.activeSessionId);
      console.log({ sessionSize: '⚖️⚖️⚖️', size });
    }
  }

  public logSpace() {
    if (this.activeSessionId) {
      const sessionJSON = this.sessionToJSON(this.activeSessionId);
      console.log({ sessionSize: '✒️✒️✒️', sessionJSON });
    }
  }

  public downloadJSON() {
    if (this.activeSessionId) {
      const size = this.getSessionSize(this.activeSessionId);
      const sessionJSON = this.sessionToJSON(this.activeSessionId);
      const sessionData = { sessionId: this.activeSessionId, size, sessionJSON };
      const sessionJsonBlob = new Blob([JSON.stringify(sessionData, null, '\t')], {
        type: 'application/json',
      });

      const saveSessionJSON = document.createElement('a');
      saveSessionJSON.href = URL.createObjectURL(sessionJsonBlob);
      saveSessionJSON.download = `${this.activeSessionId} clone.json`;
      saveSessionJSON.click();
    }
  }

  public downloadBase64String() {
    if (this.activeSessionId) {
      const base64EncodedSpace = this.sessionToBase64(this.activeSessionId);
      const sessionTextBlob = new Blob([base64EncodedSpace], { type: 'text/plain' });
      const saveSessionText = document.createElement('a');
      saveSessionText.href = URL.createObjectURL(sessionTextBlob);
      saveSessionText.download = `${this.activeSessionId} Base64 clone.txt`;
      saveSessionText.click();
    }
  }

  public downloadBinary() {
    if (!this.activeSessionId) {
      return;
    }
    const binarySpace = this.ySessions.get(this.activeSessionId).getYDocAsBinary();
    const sessionBinaryBlob = new Blob([binarySpace], { type: 'application/octet-stream' });
    const saveSessionBinary = document.createElement('a');
    saveSessionBinary.href = URL.createObjectURL(sessionBinaryBlob);
    saveSessionBinary.download = `${this.activeSessionId} Binary clone`;
    saveSessionBinary.click();
  }

  private connectWebSocket() {
    this.socket?.connect();
  }

  private onReconnectError = (error) => {
    this.telemetry.event('Socket reconnect error', { error });
    this.networkService.wbServer.next(ConnectionStatus.DISCONNECTED);
  };

  private onReconnectAttempt = async (attempt) => {
    const clientNetworkState = await this.networkService.checkInternetAndGcpConnection(attempt);
    this.logAndReportClientNetworkState(clientNetworkState);

    if (this._reconnectAttempt === 3) {
      this.showReconnectingToServerNotification();
      this.networkStatus.next(NetworkStatus.DISCONNECTED);
    }

    this._reconnectAttempt += 1;
    this.telemetry.event('Socket reconnect attempt', { attempt });
  };

  private onReconnect = (attempt) => {
    this.telemetry.event('Socket reconnect successful', { attempt });

    // Emit the reconnect event immediately (without delay)
    this.wbSocketEventSubject.next(SocketEventType.RECONNECT);

    modifiedSetTimeout(() => {
      this.sendSyncWithServer(this.activeSessionId, true);
      this.syncUserPresence('reconnected');
      this._syncSessionDataSubject.next();
    }, 1000);
  };

  private syncUserPresence(event: 'reconnected' | 'disconnected') {
    this.updateUserPresence.next(event);
  }

  private onReconnectFailed = () => {
    this.telemetry.event('reconnect_failed', {});
  };

  private isWsWorkerEnabled(): boolean {
    // Disable Web worker if its not supported by the browser
    if (typeof Worker === 'undefined') {
      return false;
    }
    return this.flagsService.isFlagEnabled(FLAGS.WEBSOCKET_WEB_WORKER);
  }

  public logSpaceLoadingTimeToFS(userIsRefreshing = false) {
    // handle race conditions if animationStartTime is not set by setActiveSession
    if (!this.animationStartTime) {
      return;
    }
    let timeElapsed = (new Date().getTime() - this.animationStartTime) / 1000;
    timeElapsed = Math.round((timeElapsed + Number.EPSILON) * 10) / 10; // round to 1 decimal place
    const spaceIsLoading = this.sharedDataService.dataLoading.getValue();
    if (!spaceIsLoading && !userIsRefreshing) {
      this.telemetry.event('[Spaces load time]', { animationDuration: timeElapsed });
    } else if (spaceIsLoading && userIsRefreshing) {
      this.telemetry.event('[User waiting time before refreshing]', {
        animationDuration: timeElapsed,
      });
    }
  }

  public getCurrentAnalyticsSession(): void {
    if (!this.activeSessionId) {
      return;
    }
    this.socket?.emit(WebSocketEventType.GET_CURRENT_SESSION, this.activeSessionId);
  }

  private logAndReportClientNetworkState(clientNetworkState: ClientNetworkState) {
    if (
      clientNetworkState.globalEndpointSuccess >= this.CONSISTENCY_THRESHOLD &&
      clientNetworkState.gcpEndpointSuccess >= this.CONSISTENCY_THRESHOLD
    ) {
      clientNetworkState.disconnectionReason = 'Socket server is not reachable';
    }
    if (clientNetworkState.globalEndpointFailure >= this.CONSISTENCY_THRESHOLD) {
      clientNetworkState.disconnectionReason = 'No internet connection';
    } else if (clientNetworkState.gcpEndpointFailure >= this.CONSISTENCY_THRESHOLD) {
      clientNetworkState.disconnectionReason = 'Cannot access GCP';
    }

    this.telemetry.event('Network disconnected state', clientNetworkState);
  }

  private getGcsDownloadRetryDelayMs(retryCount: number): number {
    return Math.min(
      Math.pow(2, retryCount - 1) * this.GCS_DOWNLOAD_BACKOFF_BASE_MS,
      this.GCS_DOWNLOAD_BACKOFF_MAX_MS,
    );
  }

  private dismissGcsFailureModal(): void {
    this.gcsFailureModalRef?.close();
  }

  private handleGcsSpaceDownload(gcsSpaceDownload: GcsSpaceDownload): void {
    this.getYjsUpdateFromGCS(gcsSpaceDownload)
      .pipe(
        retry({
          count: this.GCS_DOWNLOAD_MAX_RETRIES,
          delay: (_error, retryCount) => modifiedTimer(this.getGcsDownloadRetryDelayMs(retryCount)),
        }),
        take(1),
      )
      .subscribe({
        next: (update) => this.applyGcsSpaceDownload(gcsSpaceDownload, update),
        error: (error) => this.handleGcsSpaceDownloadFailure(gcsSpaceDownload.sessionId, error),
      });
  }

  private applyGcsSpaceDownload(
    gcsSpaceDownload: GcsSpaceDownload,
    update: Uint8Array | null,
  ): void {
    this.spaceRepo.updateSpace(gcsSpaceDownload.sessionId, {
      initialGCSSpaceSize: update?.byteLength ?? 0,
    });
    if (update) {
      this._applyUpdateAndRemovePendingUpdates({
        sessionId: gcsSpaceDownload.sessionId,
        documentUpdate: {
          yjs_update: update,
        },
      });
    }

    this.telemetry.incrementMetric(CountedMetric.yDocRx);
    const ySession = this.ySessions.get(gcsSpaceDownload.sessionId);
    this.telemetry.setSessionVars({ docClientID: ySession.getYDoc().clientID });
    this.spaceRepo.updateSpaceUI(gcsSpaceDownload.sessionId, {
      isInitialSpaceStateLoaded: true,
    });
    this.joinedRooms.push(gcsSpaceDownload.sessionId);
    this.sendSyncWithServer(gcsSpaceDownload.sessionId, false, true);
  }

  private handleGcsSpaceDownloadFailure(sessionId: string, error: unknown): void {
    this.telemetry.logObject(
      {
        sessionId,
        retryCount: this.GCS_DOWNLOAD_MAX_RETRIES,
        error,
      },
      'GCS space download failed after retries',
      'error',
    );
    if (this.activeSessionId !== sessionId) {
      return;
    }
    this.showGcsFailureModal();
  }

  private showGcsFailureModal(): void {
    if (this.gcsFailureModalRef) {
      return;
    }

    this.zone.run(() => {
      this.gcsFailureModalRef = this.matDialog.open(ConfirmationModalComponent, {
        disableClose: true,
        data: {
          title: this.translateService.instant('Unable to load space'),
          description: this.translateService.instant(
            'Something went wrong while loading this space. Please check your connection and try again.',
          ),
          confirmButtonConfig: {
            icon: 'refresh',
            iconType: 'mat-icon',
            label: this.translateService.instant('Retry'),
          },
        },
      });

      this.gcsFailureModalRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((result: 'confirm' | undefined) => {
          this.gcsFailureModalRef = undefined;

          if (result !== 'confirm') {
            return;
          }

          this.retryGcsLoad();
        });
    });
  }

  private retryGcsLoad(): void {
    if (!this.activeSessionId) {
      return;
    }

    this._spaceDownloadProgress$.next(0);
    this.sharedDataService.dataLoading.next(true);
    this.joinRooms([this.activeSessionId]);
  }

  /*
   * GCS Bucket CORS Configurations.
   * [
   *   {
   *    "origin": ["https://my.pencilapp.com", "https://canary.pencilapp.com"],
   *    "method": ["GET"],
   *    "responseHeader": ["*"],
   *    "maxAgeSeconds": 3600
   *  }
   * ]
   */
  private getYjsUpdateFromGCS(gcsSpaceDownload: GcsSpaceDownload): Observable<Uint8Array | null> {
    if (!gcsSpaceDownload.exists || !gcsSpaceDownload.gcsUrl) {
      // no backup file exist for the space
      return of(null);
    }
    const headers = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }).set(
        ErrorInterceptorSkipHeader,
        '',
      ),
    };

    return this.http
      .get(gcsSpaceDownload.gcsUrl, {
        ...headers,
        responseType: 'arraybuffer',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
              const percentDone = Math.round((100 * event.loaded) / event.total);
              this._spaceDownloadProgress$.next(percentDone);
            }
          } else if (event.type === HttpEventType.Response) {
            this._spaceDownloadProgress$.next(100);
          }
        }),
      )
      .pipe(
        filter(
          (event): event is HttpResponse<ArrayBuffer> => event.type === HttpEventType.Response,
        ),
      )
      .pipe(map((event) => event.body))
      .pipe(map((fileData) => (fileData ? new Uint8Array(fileData) : null)));
  }

  public addAiGeneratedContent(content: AiContentProcessor) {
    if (!this.activeSessionId) {
      return;
    }
    this.ySessions.get(this.activeSessionId).insertAiGeneratedContent(content);
    this.calcDiffAndSendUpdate(this.activeSessionId);
  }

  /**
   * Converts a FrameItem to a YCanvasItemObject, item.fresh is set to false
   */
  public convertFrameItemToYCanvasItem(frameItem: FrameItem): YCanvasItemObject {
    return convertFrameItemToYCanvasItem(frameItem, this.userService.userId);
  }

  public calcDiffAndSendUpdate(sessionId: string, throttle = false): void {
    if (this.constantMemoryApiSpaceSyncSettingsService.shouldSendUpdatesBasedOnDocChanges) {
      return;
    }
    if (throttle) {
      this.throttledCreateAndSendUpdate(sessionId);
      return;
    }
    const update = this.ySessions.get(sessionId).getUpdate();
    const serverUpdate = this.createYUpdate(update, sessionId);
    if (!serverUpdate) {
      return;
    }
    this.sendUpdateToServer('update', serverUpdate);
  }

  /**
   * Sets up a listener for Y.doc updates to send changes to the server
   * @param ySession The YSession instance
   * @param sessionId The session ID
   */
  private setupYDocUpdateListener(ySession: YSession, sessionId: string): void {
    ySession.getYDoc().on('update', (update, origin) => {
      if (origin !== SERVER_ORIGIN) {
        const sessionUpdate = this.createYUpdate(update, sessionId);
        this.sendUpdateToServer('update', sessionUpdate);
      }
    });
  }

  private throttledCreateAndSendUpdate(sessionId: string) {
    if (!this.debouncedSendYUpdateHandlers.has(sessionId)) {
      this.debouncedSendYUpdateHandlers.set(
        sessionId,
        _.throttle(() => this.sendToServer(sessionId), this.socketEventProcessRate, {
          // We need only to send the update at the end of the first second inorder to send the latest state at each second
          trailing: true,
          leading: false,
        }),
      );
    }
    this.debouncedSendYUpdateHandlers.get(sessionId)();
  }

  sendToServer(sessionId: string) {
    const update = this.createYUpdate(this.getYSessionById(sessionId).getUpdate(), sessionId);
    update && this.sendUpdateToServer('update', update);
  }

  public getYSessionById(sessionId): YSession {
    return this.ySessions.get(sessionId);
  }

  public getSocketEventProcessRate() {
    return this.socketEventProcessRate;
  }
}
